import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import PropertyCard from '../components/PropertyCard.jsx';
import { FilterPanel } from '../components/SearchControls.jsx';
import MapPanel from '../components/MapPanel.jsx';
import { EmptyState, ErrorState, Pagination, Spinner } from '../components/ui.jsx';
import { useFavoriteToggle, useFetch, useTitle } from '../lib/hooks.js';
import { toQuery } from '../api/client.js';
import { pluralise } from '../lib/format.js';

const SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'price_asc', label: 'Rent, low to high' },
  { value: 'price_desc', label: 'Rent, high to low' },
  { value: 'rooms_desc', label: 'Most rooms' },
  { value: 'popular', label: 'Most viewed' }
];

// The URL is the source of truth for a search, so results can be shared, and the
// back button behaves the way people expect.
const filtersFromUrl = (params) => ({
  q: params.get('q') || '',
  city: params.get('city') || '',
  region: params.get('region') || '',
  minPrice: params.get('minPrice') || '',
  maxPrice: params.get('maxPrice') || '',
  rooms: params.get('rooms') || '',
  type: params.get('type') || '',
  furnished: params.get('furnished') || '',
  letting: params.get('letting') || '',
  // To rent unless asked otherwise: the marketplace started as rentals, and
  // every link into it from before selling existed means rentals.
  kind: params.get('kind') === 'sale' ? 'sale' : 'rent',
  available: params.get('available') || '',
  amenities: params.get('amenities') ? params.get('amenities').split(',') : [],
  sort: params.get('sort') || 'newest',
  page: Number(params.get('page') || 1)
});

const Search = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const filters = useMemo(() => filtersFromUrl(params), [params]);
  const [view, setView] = useState('grid');

  const sale = filters.kind === 'sale';
  const noun = sale ? 'Houses and land for sale' : 'Houses to rent';
  useTitle(filters.city ? `${noun} in ${filters.city}` : noun);

  const query = toQuery(filters);
  const { data, loading, error, reload, setData } = useFetch(`/properties${query}`);
  const mapQuery = toQuery({ ...filters, page: undefined, pageSize: undefined });
  const onMap = view === 'map' || view === 'satellite';
  const map = useFetch(onMap ? `/properties/map${mapQuery}` : null, [onMap]);

  const applyFilters = useCallback(
    (next) => {
      setParams(new URLSearchParams(toQuery(next).replace(/^\?/, '')));
    },
    [setParams]
  );

  const onFavorite = useFavoriteToggle((id, favorited) => {
    if (!data) return;
    setData({ ...data, items: data.items.map((item) => (item.id === id ? { ...item, favorited } : item)) });
  });

  const activeCount = [
    filters.q,
    filters.city,
    filters.region,
    filters.minPrice,
    filters.maxPrice,
    filters.rooms,
    filters.type,
    filters.furnished,
    filters.letting,
    filters.available,
    ...(filters.amenities || [])
  ].filter(Boolean).length;

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>{filters.city ? `${noun} in ${filters.city}` : noun}</h1>
          <div className="tab-row kind-tabs" role="tablist" aria-label="Rent or buy">
            {[
              ['rent', 'To rent'],
              ['sale', 'For sale']
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={filters.kind === value}
                className={`tab ${filters.kind === value ? 'is-current' : ''}`}
                // Prices mean different things to rent and to buy, so a switch
                // drops the price range and the letting filter with it.
                onClick={() => applyFilters({ ...filters, kind: value, minPrice: '', maxPrice: '', letting: '', page: 1 })}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="muted">
            {loading && !data
              ? 'Searching...'
              : data
                ? `${pluralise(data.total, 'listing')} found${activeCount ? ` with ${activeCount} filter${activeCount === 1 ? '' : 's'} applied` : ''}`
                : ''}
          </p>
        </div>

        <div className="head-controls">
          <label className="inline-select">
            <span>Sort</span>
            <select value={filters.sort} onChange={(event) => applyFilters({ ...filters, sort: event.target.value, page: 1 })}>
              {SORTS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <div className="view-toggle" role="group" aria-label="View">
            <button
              type="button"
              className={view === 'grid' ? 'is-current' : ''}
              onClick={() => setView('grid')}
              aria-pressed={view === 'grid'}
            >
              List
            </button>
            <button
              type="button"
              className={view === 'map' ? 'is-current' : ''}
              onClick={() => setView('map')}
              aria-pressed={view === 'map'}
            >
              Map
            </button>
            <button
              type="button"
              className={view === 'satellite' ? 'is-current' : ''}
              onClick={() => setView('satellite')}
              aria-pressed={view === 'satellite'}
            >
              Satellite
            </button>
          </div>
        </div>
      </header>

      <FilterPanel
        value={filters}
        onChange={applyFilters}
        onReset={() => setParams(new URLSearchParams())}
        resultCount={data?.total}
      />

      {onMap ? (
        <section className="map-section">
          {map.loading ? <Spinner /> : null}
          {map.error ? <ErrorState message={map.error} onRetry={map.reload} /> : null}
          {map.data ? (
            <>
              <MapPanel
                points={map.data.items}
                height={520}
                basemap={view}
                onSelect={(id) => navigate(`/properties/${id}`)}
              />
              <p className="map-note">
                {pluralise(map.data.items.length, 'listing')} on the map. Pins sit on the neighbourhood, not the exact
                door, until you agree a viewing with the landlord.
              </p>
            </>
          ) : null}
        </section>
      ) : (
        <>
          {loading && !data ? <Spinner /> : null}
          {error ? <ErrorState message={error} onRetry={reload} /> : null}

          {data && data.items.length === 0 ? (
            <EmptyState
              title="Nothing matches that search"
              message="Try widening the rent range, removing a filter or two, or looking at a nearby town."
              action={
                <button type="button" className="btn btn-outline" onClick={() => setParams(new URLSearchParams())}>
                  Clear all filters
                </button>
              }
            />
          ) : null}

          <div className={`card-grid ${loading ? 'is-refreshing' : ''}`}>
            {data?.items.map((property) => (
              <PropertyCard key={property.id} property={property} onToggleFavorite={onFavorite} />
            ))}
          </div>

          {data ? (
            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              onChange={(page) => applyFilters({ ...filters, page })}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default Search;
